import { useContext, useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { useHotkeys, usePrevious, useDebouncedValue } from '@mantine/hooks';
import { useUserConfigQuery } from './useUserConfigQuery';
import { useQueryClient } from '@tanstack/react-query';

import { useAuth } from '@/context/AuthContext';
import { useUserConfigState, useUserConfigDispatch } from '@/context/UserConfigContext';

import { toast } from 'sonner';
import { sanitizeUserConfig } from '@/utils/sanitizeConfig';
import { ValidationContext } from '@/components/validation';
import Logger from '@/utils/logger';
import type { UserConfigurationState } from '@/types/userConfiguration';
// Helper function to create a comparable version of state for consistent comparisons
// This normalizes arrays and objects to prevent false "stale data" warnings caused by
// differences in ordering between what we save and what we get back from the database
const getComparableStateJSON = (state: UserConfigurationState | null): string => {
  if (!state) return JSON.stringify(state);

  const comparable = { ...state };

  // For holiday IDs, sort for consistent ordering
  if (comparable.holidayCatalogIds && Array.isArray(comparable.holidayCatalogIds)) {
    comparable.holidayCatalogIds = [...comparable.holidayCatalogIds].sort();
  }
  if (comparable.holidayCustomIds && Array.isArray(comparable.holidayCustomIds)) {
    comparable.holidayCustomIds = [...comparable.holidayCustomIds].sort();
  }

  // Sort roles by a consistent key combination
  if (comparable.roles && Array.isArray(comparable.roles)) {
    comparable.roles = [...comparable.roles].sort((a, b) => (a.name + a.billingStructure).localeCompare(b.name + b.billingStructure));
  }
  
  // Sort rate multipliers by code
  if (comparable.rateMultipliers && Array.isArray(comparable.rateMultipliers)) {
    comparable.rateMultipliers = [...comparable.rateMultipliers].sort((a, b) => a.code.localeCompare(b.code));
  }
  
  // Sort reminder days numerically
  if (comparable.reminderDays && Array.isArray(comparable.reminderDays)) {
    comparable.reminderDays = [...comparable.reminderDays].sort((a, b) => a - b);
  }
  
  // Sort cancellation policy by days
  if (comparable.cancellationPolicy && Array.isArray(comparable.cancellationPolicy)) {
    comparable.cancellationPolicy = [...comparable.cancellationPolicy].sort((a, b) => (a.days || 0) - (b.days || 0));
  }
  
  return JSON.stringify(comparable);
};

export const useProfile = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const state = useUserConfigState();
  const [debouncedComparableState] = useDebouncedValue(getComparableStateJSON(state), 100);
  const { setFullState } = useUserConfigDispatch();

  const validationCtx = useContext(ValidationContext);
  const { data: config, isLoading, saveConfig, isSaving } = useUserConfigQuery(user?.id);
  const [activeTab, setActiveTab] = useState('profile');
  const [direction, setDirection] = useState<'left'|'right'>('right');
  const [deltaX, setDeltaX] = useState(0);

  // ── Unsaved-changes tracking ───────────────────────────────
  const initialStateRef = useRef<UserConfigurationState | null>(null);
  const [isDataStale, setIsDataStale] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const justSaved = useRef(false);

  // Keep Sonner toasts above the floating "Save Changes" button using a CSS variable
  useEffect(() => {
    const updateOffset = () => {
      const isMobileQuery = window.matchMedia("(max-width: 639px)");
      const extra = hasUnsavedChanges
        ? (isMobileQuery.matches ? '90px' : '72px') // Tweak if you change button height
        : '0px';
      document.documentElement.style.setProperty('--toast-extra-offset', extra);
    };

    updateOffset(); // Set initial value

    const mediaQueryList = window.matchMedia("(max-width: 639px)");
    mediaQueryList.addEventListener('change', updateOffset);

    return () => {
      document.documentElement.style.setProperty('--toast-extra-offset', '0px');
      mediaQueryList.removeEventListener('change', updateOffset);
    };
  }, [hasUnsavedChanges]);

  // Invoice, Multipliers, and Travel screens are now rolled into Contract
  const tabsOrder = useMemo(() => ['profile', 'contract', 'invoicing'], []);

  const previousActiveTab = usePrevious(activeTab);
  
  // Tab navigation effects
  useEffect(() => {
    if (previousActiveTab) {
      const newIndex = tabsOrder.indexOf(activeTab);
      const oldIndex = tabsOrder.indexOf(previousActiveTab);
      setDirection(newIndex > oldIndex ? 'left' : 'right');
    }
  }, [activeTab, previousActiveTab, tabsOrder]);

  const revertChanges = useCallback((options: { silent?: boolean } = {}) => {
    const stateToRevertTo = config || initialStateRef.current;
    if (stateToRevertTo) {
      setFullState(stateToRevertTo);
      initialStateRef.current = stateToRevertTo;
      setHasUnsavedChanges(false);
      setIsDataStale(false);
      toast.dismiss("stale-data-toast");
      if (!options.silent) {
        toast.info("Unsaved changes have been reverted.");
      }
    }
  }, [config, setFullState]);

  useEffect(() => {
    if (config) {
      // If there are unsaved changes, we don't want to overwrite them with a background refetch.
      if (!hasUnsavedChanges) {
        setFullState(config);
        initialStateRef.current = config; // Update the baseline "clean" state
        if (isDataStale) {
          setIsDataStale(false);
          toast.dismiss("stale-data-toast");
        }
      } else {
        const isStaleNow = initialStateRef.current && getComparableStateJSON(config) !== getComparableStateJSON(initialStateRef.current);

        if (isStaleNow && !isDataStale) {
          Logger.warn('Data loading: background refetch occurred but was ignored due to unsaved changes. Data is now stale.', { 
            component: 'useProfile'
          });
          
          setIsDataStale(true);
          toast.warning("Your configuration has been updated elsewhere.", {
            description: "You have unsaved changes. Revert to load the latest data, or save to overwrite.",
            action: {
              label: "Revert",
              onClick: () => revertChanges(),
            },
            duration: Infinity,
            id: "stale-data-toast",
          });
        }
      }
    }
  }, [config, hasUnsavedChanges, setFullState, revertChanges, isDataStale]);

  // Effect to detect unsaved changes by comparing working state with the last known clean state.
  // Use a debounced serialized snapshot to catch in-place mutations too.
  useEffect(() => {
    if (justSaved.current) {
      return;
    }
    if (initialStateRef.current) {
      const baseline = getComparableStateJSON(initialStateRef.current);
      const changed = baseline !== debouncedComparableState;

      // Log only when the status changes to avoid spamming the console
      setHasUnsavedChanges(changed);
    }
  }, [debouncedComparableState, hasUnsavedChanges]);

  // Data saving
  const handleSave = async (section: string = activeTab) => {
    if (validationCtx) {
      const validationErrors = validationCtx.getActiveErrors(section);
      if (validationErrors.length > 0) {
        Logger.info('Validation failed during profile update', {
          component: 'useProfile',
          userId: user?.id,
          errors: validationErrors
        });
        toast.error(validationErrors[0].message);
        return; // Return explicitly to avoid proceeding
      }
    }

    try {
      const stateToSave = sanitizeUserConfig(state);
      const savedData = await saveConfig(stateToSave);

      // --- Start of Critical Section ---
      // This order prevents both the stale data toast and the reappearing save bar.
      justSaved.current = true; // 1. Set a flag to disable unsaved changes detection temporarily.
      setHasUnsavedChanges(false); // 2. Immediately hide the save bar.

      // 3. Update all states to be consistent with the sanitized data that was saved.
      setFullState(savedData);
      initialStateRef.current = savedData;
      queryClient.setQueryData(['userConfig', user?.id], savedData);

      // 4. Invalidate for background consistency check.
      queryClient.invalidateQueries({ queryKey: ['userConfig', user?.id] });

      // 5. Re-enable unsaved changes detection after the debounce period.
      setTimeout(() => { justSaved.current = false; }, 600);
    } catch (error) {
      const err = error as Error;
      Logger.error('Error saving profile:', err, { component: 'useProfile' });
      if (err.message.includes('is referenced by one or more clients')) {
        toast.error('Cannot delete a role that is in use.', {
          description: "One or more roles you tried to remove are part of a client's configuration. Please remove them from all clients before deleting them from your profile."
        });
        // Revert the state to undo the failed deletion from the UI
        revertChanges({ silent: true });
      } else {
        toast.error("Failed to save configuration.", {
          description: err.message || "An unknown error occurred."
        });
      }
    }
  };

  useHotkeys([
    ['mod+S', (event) => {
      event.preventDefault();
      if (hasUnsavedChanges) {
        handleSave();
      }
    }],
  ]);

  return {
    isSaving,
    isLoading,
    activeTab,
    setActiveTab,
    deltaX,
    setDeltaX,
    tabsOrder,
    handleSave,
    direction,
    hasUnsavedChanges,
    revertChanges
  };
};
